/**
 * MonetizationService — Faithful React port of monetization.service.ts
 * Handles: Google Play Billing, AdMob, Premium state, VIP patrons
 * 
 * Web: Mock billing for development/testing
 * Native (Capacitor): Real Google Play Billing via @capgo/native-purchases
 */
import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';

// ─── Product & Purchase Types ───
export const PRODUCT_CATALOG = [
  { id: 'premium_muhurtham', basePlanId: 'muhurtham-yearly', title: 'Premium — முஹூர்த்தம்', description: 'நல்லநாள், சிறப்பு முஹூர்த்தம், 28-சுபம்', feature: 'muhurtham' },
  { id: 'premium_jathagam', basePlanId: 'jathagam-yearly', title: 'ஜாதகம் / Jathagam', description: 'ஜாதகம், பொருத்தம், லக்னம்', feature: 'jathagam' },
  { id: 'premium_panchangam', basePlanId: 'panchangam-yearly', title: 'பஞ்சாங்கம் / Panchangam', description: '10yr archive, Next 2yrs, Oonams PDF', feature: 'panchangam' },
  { id: 'remove_ads', basePlanId: 'remove-ads-yearly', title: 'Ad-Free மட்டும்', description: 'No advertisements, free pages only', feature: 'noAds' },
  { id: 'premium_all', basePlanId: 'premium-all-yearly', title: 'Above All - Ad Free', description: 'Muhurtham + Jathagam + Panchangam + Ad-Free', feature: null },
];

// Locale-aware default prices
const DEFAULT_PRICES = {
  INR: {
    premium_muhurtham: { price: '₹200.00', currency: 'INR', micros: 200000000 },
    premium_jathagam: { price: '₹200.00', currency: 'INR', micros: 200000000 },
    premium_panchangam: { price: '₹200.00', currency: 'INR', micros: 200000000 },
    remove_ads: { price: '₹100.00', currency: 'INR', micros: 100000000 },
    premium_all: { price: '₹500.00', currency: 'INR', micros: 500000000 },
  },
  USD: {
    premium_muhurtham: { price: '$2.99', currency: 'USD', micros: 2990000 },
    premium_jathagam: { price: '$2.99', currency: 'USD', micros: 2990000 },
    premium_panchangam: { price: '$2.99', currency: 'USD', micros: 2990000 },
    remove_ads: { price: '$1.49', currency: 'USD', micros: 1490000 },
    premium_all: { price: '$6.99', currency: 'USD', micros: 6990000 },
  },
  GBP: {
    premium_muhurtham: { price: '£2.49', currency: 'GBP', micros: 2490000 },
    premium_jathagam: { price: '£2.49', currency: 'GBP', micros: 2490000 },
    premium_panchangam: { price: '£2.49', currency: 'GBP', micros: 2490000 },
    remove_ads: { price: '£1.29', currency: 'GBP', micros: 1290000 },
    premium_all: { price: '£5.99', currency: 'GBP', micros: 5990000 },
  }
};

// VIP Patrons (same as Ionic app)
const VIP_PATRONS = [
  'drnvs@outlook.com',
  's.sathyanarayanan@gmail.com'
];

// AdMob IDs
const AD_IDS = {
  production: {
    banner: 'ca-app-pub-9042503749164991/4065317833',
    interstitial: 'ca-app-pub-9042503749164991/2470545423',
    reward: 'ca-app-pub-9042503749164991/3400483715'
  },
  test: {
    banner: 'ca-app-pub-3940256099942544/6300978111',
    interstitial: 'ca-app-pub-3940256099942544/1033173712',
    reward: 'ca-app-pub-3940256099942544/5224354917'
  }
};

// ─── Cache Keys ───
const CACHE_KEY = 'paranthaman_premium_cache';
const PRICE_CACHE_KEY = 'paranthaman_price_cache';
const CACHE_EXPIRY_DAYS = 365;

// ─── Helper Functions ───
function detectCurrency() {
  try {
    const lang = (navigator.language || 'en-IN').toLowerCase();
    if (lang.includes('us') || lang === 'en') return 'USD';
    if (lang.includes('gb') || lang.includes('uk')) return 'GBP';
    return 'INR';
  } catch { return 'INR'; }
}

function loadPriceCache() {
  try {
    const raw = localStorage.getItem(PRICE_CACHE_KEY);
    if (!raw) return null;
    const cache = JSON.parse(raw);
    const firstKey = Object.keys(cache)[0];
    if (firstKey && cache[firstKey].savedAt && (Date.now() - cache[firstKey].savedAt) > 30 * 86400000) {
      return null;
    }
    return cache;
  } catch { return null; }
}

function savePriceCache(products) {
  try {
    const cache = {};
    products.forEach(p => {
      cache[p.identifier] = {
        price: p.price, currency: p.currency,
        micros: p.priceAmountMicros, savedAt: Date.now()
      };
    });
    localStorage.setItem(PRICE_CACHE_KEY, JSON.stringify(cache));
  } catch (e) { console.warn('[PriceCache] Save failed:', e); }
}

function loadPremiumCache() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const cache = JSON.parse(raw);
    if (!cache || !cache.expiresAt) return null;
    if (Date.now() > cache.expiresAt) {
      localStorage.removeItem(CACHE_KEY);
      return null;
    }
    return cache;
  } catch { return null; }
}

function savePremiumCache(features, isPremium, hasRemovedAds, purchases) {
  try {
    const cache = {
      features, isPremium, hasRemovedAds,
      purchases: purchases.map(p => ({ productId: p.productId, acknowledged: p.acknowledged })),
      savedAt: Date.now(),
      expiresAt: Date.now() + (CACHE_EXPIRY_DAYS * 24 * 60 * 60 * 1000)
    };
    localStorage.setItem(CACHE_KEY, JSON.stringify(cache));
  } catch (e) { console.warn('[PremiumCache] Save failed:', e); }
}

function initStaticProducts() {
  const cachedPrices = loadPriceCache();
  const localeCurrency = detectCurrency();
  const defaultPrices = DEFAULT_PRICES[localeCurrency] || DEFAULT_PRICES['INR'];

  return PRODUCT_CATALOG.map(cat => {
    const cached = cachedPrices?.[cat.id];
    const fallback = defaultPrices[cat.id];
    return {
      identifier: cat.id,
      title: cat.title,
      description: cat.description,
      price: cached?.price || fallback.price,
      currency: cached?.currency || fallback.currency,
      type: 'subs',
      priceAmountMicros: cached?.micros || fallback.micros,
      planIdentifier: cat.basePlanId,
    };
  });
}

function normalizeProductId(rawId) {
  if (!rawId) return rawId;
  const id = rawId.toLowerCase();
  if (['premium_muhurtham', 'premium_jathagam', 'premium_panchangam', 'premium_all', 'remove_ads'].includes(id)) return id;
  if (id.includes('muhurtham')) return 'premium_muhurtham';
  if (id.includes('jathagam')) return 'premium_jathagam';
  if (id.includes('panchangam')) return 'premium_panchangam';
  if (id.includes('remove') && id.includes('ad')) return 'remove_ads';
  if (id.includes('premium') && id.includes('all')) return 'premium_all';
  return rawId;
}

// ─── React Context ───
const MonetizationContext = createContext(null);

export const MonetizationProvider = ({ children }) => {
  const [products, setProducts] = useState(() => initStaticProducts());
  const [purchases, setPurchases] = useState([]);
  const [premiumFeatures, setPremiumFeatures] = useState({ muhurtham: false, jathagam: false, panchangam: false, noAds: false });
  const [isPremium, setIsPremium] = useState(false);
  const [hasRemovedAds, setHasRemovedAds] = useState(false);
  const [productsLoaded, setProductsLoaded] = useState(true);
  const purchasesRef = useRef(purchases);
  purchasesRef.current = purchases;

  // Check VIP status
  const isVipPatron = useCallback((email) => {
    return VIP_PATRONS.includes(email?.toLowerCase());
  }, []);

  // Refresh premium state from purchases
  const refreshPremiumState = useCallback((currentPurchases, vipEmail) => {
    const isVip = isVipPatron(vipEmail);
    const hasProd = (pid) => currentPurchases.some(p => normalizeProductId(p.productId) === pid);
    const hasAll = isVip || hasProd('premium_all');
    const premium = isVip || hasAll || hasProd('premium_muhurtham') || hasProd('premium_jathagam') || hasProd('premium_panchangam');
    const removedAds = isVip || hasAll || hasProd('remove_ads');

    const features = {
      muhurtham: hasAll || hasProd('premium_muhurtham'),
      jathagam: hasAll || hasProd('premium_jathagam'),
      panchangam: hasAll || hasProd('premium_panchangam'),
      noAds: removedAds
    };

    setPremiumFeatures(features);
    setIsPremium(premium);
    setHasRemovedAds(removedAds);
    savePremiumCache(features, premium, removedAds, currentPurchases);
    return features;
  }, [isVipPatron]);

  // Load from cache on mount
  useEffect(() => {
    const cache = loadPremiumCache();
    if (cache) {
      if (cache.features) setPremiumFeatures(cache.features);
      if (cache.isPremium !== undefined) setIsPremium(cache.isPremium);
      if (cache.hasRemovedAds !== undefined) setHasRemovedAds(cache.hasRemovedAds);
      if (cache.purchases) {
        const restored = cache.purchases.map(p => ({
          productId: p.productId, transactionId: 'cached',
          transactionDate: new Date(cache.savedAt).toISOString(),
          transactionReceipt: '', signature: '', acknowledged: p.acknowledged !== false
        }));
        setPurchases(restored);
      }
      console.log('[Premium] Loaded from cache:', JSON.stringify(cache.features));
    }
  }, []);

  // Purchase product (web mock — native will use Capacitor plugin)
  const purchaseProduct = useCallback(async (productId) => {
    console.log('[Purchase] Starting:', productId);
    const product = products.find(p => p.identifier === productId);
    if (!product) throw new Error('Product not found: ' + productId);

    // Web simulation
    const purchase = {
      productId,
      transactionId: 'txn_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9),
      transactionDate: new Date().toISOString(),
      transactionReceipt: 'receipt_' + Date.now(),
      signature: 'signature_' + Math.random().toString(36).substr(2, 16),
      acknowledged: true,
    };

    const newPurchases = [...purchasesRef.current, purchase];
    setPurchases(newPurchases);
    refreshPremiumState(newPurchases);
    console.log('[Purchase] SUCCESS:', productId);
    return true;
  }, [products, refreshPremiumState]);

  // Restore purchases
  const restorePurchases = useCallback(async () => {
    console.log('[Restore] Web mode — checking localStorage');
    const cache = loadPremiumCache();
    if (cache?.purchases) {
      const restored = cache.purchases.map(p => ({
        productId: p.productId, transactionId: 'restored',
        transactionDate: new Date().toISOString(),
        transactionReceipt: '', signature: '', acknowledged: true
      }));
      setPurchases(restored);
      refreshPremiumState(restored);
    }
  }, [refreshPremiumState]);

  // Get price for a product
  const getPrice = useCallback((productId) => {
    const p = products.find(prod => prod.identifier === productId);
    return p?.price || '₹200.00';
  }, [products]);

  // Check if specific feature is unlocked
  const hasFeature = useCallback((feature) => {
    return premiumFeatures[feature] || false;
  }, [premiumFeatures]);

  // Apply VIP code
  const applyVipCode = useCallback((code) => {
    // VIP codes can be checked here
    if (code === 'PARANTHAMAN_VIP_2026') {
      const allPurchase = {
        productId: 'premium_all', transactionId: 'vip_' + Date.now(),
        transactionDate: new Date().toISOString(),
        transactionReceipt: 'vip', signature: 'vip', acknowledged: true
      };
      const newPurchases = [...purchasesRef.current, allPurchase];
      setPurchases(newPurchases);
      refreshPremiumState(newPurchases);
      return true;
    }
    return false;
  }, [refreshPremiumState]);

  const value = {
    products, purchases, premiumFeatures, isPremium, hasRemovedAds, productsLoaded,
    purchaseProduct, restorePurchases, getPrice, hasFeature, isVipPatron, applyVipCode,
    AD_IDS, PRODUCT_CATALOG
  };

  return (
    <MonetizationContext.Provider value={value}>
      {children}
    </MonetizationContext.Provider>
  );
};

export const useMonetization = () => {
  const ctx = useContext(MonetizationContext);
  if (!ctx) throw new Error('useMonetization must be used within MonetizationProvider');
  return ctx;
};

export default MonetizationContext;
